import { Shipment, ShipmentStatus } from "@cnx/common";
import { Box, Stack, Text } from "@mantine/core";
import {
  IconCalendarTime,
  IconMapPin,
  IconRoute,
  IconTruck,
} from "@tabler/icons-react";
import IconItem from "components/IconItem";
import ShipmentInstructions from "components/ShipmentInstructions";
import Drivers from "components/ShippingManagement/Drivers";
import ShortActivityBlock from "components/ShippingManagement/ShortActivityBlock";
import TemperaturePoints from "components/TemperaturePoints";
import IconTruckDivided from "icons/shipment/divided-truck.svg";
import IconOnLocation from "icons/shipment/on-location.svg";
import IconTruckBody from "icons/shipment/truck-body.svg";
import React from "react";
import { formatDate } from "utils/date";

interface ShipmentProps {
  trip: Shipment;
}

const TripCardPreview: React.FC<ShipmentProps> = ({ trip }) => {
  if (!trip) {
    return <></>;
  }

  const {
    beginDateTime,
    endDateTime,
    status,
    temp1,
    temp2,
    temp3,
    temp4,
    route,
    tripIdentifier,
    instruction,
    palletGroupDescription,
    trailers,
    drivers,
    trucks,
  } = trip;

  const temperatureSetPoints = [temp1, temp2, temp3, temp4].filter(Boolean);

  return (
    <Box
      p={15}
      bg={"white"}
      fz={"sm"}
      lh={"sm"}
      c={status === ShipmentStatus.FINISHED ? "gray" : "inherit"}
    >
      <Stack w={"100%"} gap={1} className="pl-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          <div>
            {beginDateTime && (
              <IconItem contentAs="div" icon={<IconCalendarTime />}>
                <Text fz={"sm"} lh={"sm"} fw={600}>
                  {formatDate(beginDateTime)}
                </Text>
              </IconItem>
            )}
          </div>

          <div>
            {endDateTime && (
              <IconItem icon={<IconOnLocation width={24} height={24} />}>
                {formatDate(endDateTime)}
              </IconItem>
            )}
          </div>

          <div>
            <IconItem
              contentAs="div"
              isShow={temperatureSetPoints.length !== 0}
              icon={
                <IconTruckDivided
                  width={24}
                  height={24}
                  className={"text-blue"}
                />
              }
            >
              <TemperaturePoints temperatureSetPoints={temperatureSetPoints} />
            </IconItem>
          </div>

          <div>
            <IconItem
              isShow={tripIdentifier?.length !== 0}
              contentAs="div"
              icon={<IconRoute />}
            >
              <Text fz={"sm"} lh={"sm"} fw={400}>
                {tripIdentifier}
              </Text>
            </IconItem>
          </div>

          <div>
            <IconItem
              isShow={trucks?.length !== 0}
              contentAs="div"
              icon={<IconTruck width={24} height={24} />}
            >
              {trucks}
            </IconItem>
          </div>

          <div>
            <Drivers drivers={drivers?.split(";") ?? []} />
          </div>

          <div>
            <IconItem
              isShow={trailers?.length !== 0}
              contentAs="div"
              icon={<IconTruckBody width={24} height={24} />}
            >
              {trailers}
            </IconItem>
          </div>
        </div>

        <ShipmentInstructions instruction={instruction} />

        <div>
          <IconItem icon={<IconMapPin />}>
            <Text span display={"block"}>
              {route}
            </Text>
          </IconItem>
        </div>

        <ShortActivityBlock
          palletGroupDescription={palletGroupDescription ?? []}
        />
      </Stack>
    </Box>
  );
};

export default TripCardPreview;
